<template>
  <div class="approvals-create-template px-6 pt-12">
    <div class="text-h1 text-textDarkest mb-4">
      Create Approval Flow
    </div>

    <p class="text-body-1 text-textDark mb-4">
      Apply an approval template to a job profile.
    </p>

    <div class="mb-4">
      <label
        for="approvals-create-template-select"
        class="text-body-3 text-textDarkest d-block mb-1"
      >Approval Template</label>
      <v-select
        id="approvals-create-template-select"
        v-model="selectedTemplateUlid"
        :items="activeTemplates"
        item-title="name"
        item-value="ulid"
        density="compact"
        variant="outlined"
        rounded="lg"
        color="primary"
        menu-icon="$chevron-down"
        hide-details
        placeholder="Select approval template"
        class="approvals-create-template-select text-body-1"
        :menu-props="{ contentClass: 'approvals-create-template-select-menu' }"
        :disabled="!activeTemplates.length || hasPendingApprovalFlows"
      >
        <template #selection>
          <ApprovalTemplateSelectOption
            v-if="selectedTemplate"
            :template="selectedTemplate"
            compact
          />
          <span
            v-else
            class="text-body-1 text-textDark"
          >Select approval template</span>
        </template>

        <template #item="{ props: itemProps, item }">
          <v-list-item
            v-bind="itemProps"
            class="approvals-create-template-select-item"
          >
            <ApprovalTemplateSelectOption :template="item.raw" />
          </v-list-item>
        </template>
      </v-select>
    </div>

    <v-btn
      block
      rounded="lg"
      color="primary"
      variant="flat"
      height="40"
      class="text-button-1"
      :disabled="!selectedTemplateUlid || hasPendingApprovalFlows"
      @click="handleContinue"
    >
      Start Approval Flow
    </v-btn>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import ApprovalTemplateSelectOption from '@/components/jobProfiles/editor/ApprovalTemplateSelectOption.vue';
import { useApprovalFlows } from '@/stores/jobProfiles/approvalFlows';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';

const approvalFlowsStore = useApprovalFlows();
const {
  activeTemplates,
  selectedTemplateUlid,
  selectedTemplate,
} = storeToRefs(approvalFlowsStore);
const { beginAssignApprovers } = approvalFlowsStore;
const { hasPendingApprovalFlows } = storeToRefs(useCurrentJobProfile());

const handleContinue = () => {
  try {
    beginAssignApprovers();
  } catch (error) {
    useAppSnackbar().addErrorSnackbar({
      text: error.message,
    });

    throw error;
  }
};
</script>

<style scoped lang="less">
.approvals-create-template-select {
  :deep(.v-field__append-inner .v-icon) {
    color: @text-dark;
  }

  :deep(.v-field) {
    color: @text-darkest;
    min-height: 72px;
    align-items: center;
  }

  :deep(.v-field__input) {
    min-height: 72px;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  :deep(.v-select__selection--placeholder) {
    color: @text-dark;
  }
}
</style>

<style lang="less">
.approvals-create-template-select-menu.v-overlay__content > .v-list {
  box-shadow: none;
  border: 1px solid @stroke-default;
  border-radius: 8px;
  background-color: @bg-default;
}

.approvals-create-template-select-menu .v-list-item {
  padding: 12px 16px;
  min-height: unset;
}

.approvals-create-template-select-menu .v-list-item-title {
  display: none;
}
</style>
